import { type ReactNode, useEffect, useState } from 'react';

/** Each visit uses the same server deadline. Authentication never starts a new window. */
export function RecoveryCountdown({
  recoverUntil,
  children,
}: { recoverUntil: string; children: ReactNode }) {
  const [now, setNow] = useState(Date.now);
  const deadline = Date.parse(recoverUntil);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const seconds = Math.max(0, Math.floor((deadline - now) / 1000));
  if (!Number.isFinite(seconds) || seconds === 0)
    return (
      <p>The recovery window has ended and permanent cleanup may have begun.</p>
    );
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  const date = new Intl.DateTimeFormat(undefined, {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }).format(new Date(deadline));
  return (
    <>
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          Time left to restore your workspace
        </p>
        <p
          role="timer"
          aria-live="off"
          className="text-2xl font-semibold tabular-nums"
        >
          {days}d {String(hours).padStart(2, '0')}h{' '}
          {String(minutes).padStart(2, '0')}m{' '}
          {String(remainder).padStart(2, '0')}s
        </p>
        <p className="text-sm text-muted-foreground">
          Permanent cleanup starts on{' '}
          <time dateTime={recoverUntil}>{date}</time>. Signing in does not
          extend this deadline.
        </p>
      </div>
      <p className="text-sm text-muted-foreground">
        Restoring keeps items that were deleted earlier in their original state.
      </p>
      {children}
    </>
  );
}
